import React, { useMemo } from 'react'

import { Anchor, Badge, Group, Tooltip } from '@tidbcloud/uikit'
import { MRT_ColumnDef } from '@tidbcloud/uikit/biz'
import { LabelTooltip } from '@tidbcloud/uikit/biz'

import TimeComponent from '../../../components/TimeComponent'
import { formatValue } from '../../migration_shared/format-value'
import { StatementModel } from '../../utils/type'

export const STATUS_MAP = {
  unbound: 'carbon.6',
  bound: 'green'
}

export const useStatementColumns = (
  onPlanClick: (planDigest: string) => void
) => {
  const columns = useMemo<MRT_ColumnDef<StatementModel>[]>(() => {
    return [
      {
        id: 'plan_digest',
        header: 'Plan Digest',
        Header: () => TIP_MAP.plan_digest,
        accessorFn: (row: StatementModel) => (
          <Tooltip label={row.plan_digest || 'N/A'} withArrow>
            <Anchor
              truncate
              sx={{ display: 'inline-block' }}
              maw={200}
              onClick={() => onPlanClick(row.plan_digest!)}
            >
              {row.plan_digest || 'N/A'}
            </Anchor>
          </Tooltip>
        )
      },
      {
        id: 'plan_in_binding',
        header: 'Binding',
        accessorFn: (row: StatementModel) => {
          const isBound = !!row.plan_in_binding
          return (
            <Group gap={0}>
              <Badge
                color={STATUS_MAP[isBound ? 'bound' : 'unbound']}
                variant='dot'
                size='lg'
                sx={{ pointerEvents: 'none' }}
              >
                {isBound ? 'Target' : 'Not target'}
              </Badge>
              <LabelTooltip
                label={
                  isBound
                    ? 'This plan is selected and used for SQL Binding.'
                    : 'This plan is not used to create SQL Binding.'
                }
              />
            </Group>
          )
        }
      },
      {
        id: 'avg_latency',
        header: 'Avg Latency',
        Header: TIP_MAP.avg_latency,
        accessorFn: (row: StatementModel) =>
          formatValue(row, 'avg_latency', 'ns')
      },
      {
        id: 'avg_total_keys',
        header: 'Avg Total Keys',
        Header: TIP_MAP.avg_total_keys,
        accessorFn: (row: StatementModel) =>
          formatValue(row, 'avg_total_keys', 'short')
      },
      {
        id: 'avg_processed_keys',
        header: 'Avg Processed Keys',
        minSize: 200,
        Header: TIP_MAP.avg_processed_keys,
        accessorFn: (row: StatementModel) =>
          formatValue(row, 'avg_processed_keys', 'short')
      },
      {
        id: 'last_seen',
        header: 'Last Active Time',
        accessorFn: (row: StatementModel) => {
          return <TimeComponent time={row.last_seen || 0} />
        }
      },
      {
        id: 'action',
        header: 'Actions',
        accessorFn: (row: StatementModel) => (
          <Anchor onClick={() => onPlanClick(row.plan_digest!)}>
            Review & Bind
          </Anchor>
        )
      }
    ] as MRT_ColumnDef<StatementModel>[]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return columns
}

export const TIP_MAP = {
  plan_digest: (
    <Group gap={0}>
      Plan Digest
      <LabelTooltip label='Plan Digest is the unique identifier of execution plans.' />
    </Group>
  ),
  avg_latency: (
    <Group gap={0}>
      Average Latency
      <LabelTooltip
        label={
          <>
            Average latency is the average execution latency of SQL statements.
            Refer to AVG_LATENCY in{' '}
            <a
              href='https://docs.pingcap.com/tidb/stable/statement-summary-tables/#statements_summary-fields-description'
              target='_blank'
              rel='noopener noreferrer'
            >
              Statement Summary Tables
            </a>
            . This is an indicator of how long the query is processed.
          </>
        }
      />
    </Group>
  ),
  avg_total_keys: (
    <Group gap={0}>
      Average Total Keys
      <LabelTooltip
        label={
          <>
            Average Total Keys is the average number of keys including MVCC
            records that Coprocessor has scanned. Refer to AVG_TOTAL_KEYS in{' '}
            <a
              href='https://docs.pingcap.com/tidb/stable/statement-summary-tables/#statements_summary-fields-description'
              target='_blank'
              rel='noopener noreferrer'
            >
              Statement Summary Tables
            </a>
            . This shows the physical rows scanned, that could vary according to
            recent DML operations.
          </>
        }
      />
    </Group>
  ),
  avg_processed_keys: (
    <Group gap={0}>
      Average Processed Keys
      <LabelTooltip
        label={
          <>
            Average Processed Keys is the average number of keys that
            Coprocessor has scanned. Refer to AVG_PROCESSED_KEYS in{' '}
            <a
              href='https://docs.pingcap.com/tidb/stable/statement-summary-tables/#statements_summary-fields-description'
              target='_blank'
              rel='noopener noreferrer'
            >
              Statement Summary Tables
            </a>
            . This shows the necessary rows to be scanned that is based on
            actual data distribution of the tables.
          </>
        }
      />
    </Group>
  )
}
